import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { ArrowRight, Check } from "lucide-react";
import { LocationModePill } from "../friendlies/FriendlyPills";
import { friendliesService } from "../../services/friendliesApi";

// In-chat "Friendly request" unfurl (design Frame 9, D-003). Rendered from the
// `friendly_request` attachment: either the browse Share (no application_id) or the
// card start_chat drops for a specific applicant (carries application_id +
// application_status + request_status — BE c73ec2d).
//
// OPEN → crest/team/date/mode/tier + two actions (Open request / Apply). CLOSED →
// muted header, actions collapse to a single "View" (never an error state).
//
// OWNER Mark-agreed CTA (M3-review F-M3-3, canvas "mark applications as agreed from
// their chat"): shown ONLY on the per-application card (application_id present) when
// the viewer is the request owner, it's a multi-match camp, the request is open, and
// this application is in_chat. mark_agreed is owner-gated server-side too.
const initials = (name = "") =>
  name.split(/\s+/).filter(Boolean).slice(0, 2).map((w) => w[0]).join("").toUpperCase() || "?";

const FriendlyRequestCard = (props) => {
  const navigate = useNavigate();
  const clubName = useSelector((s) => s.auth.user?.club_name);
  const a = props.attachments?.[0] || {};
  const card = a.friendly_request || a.metadata || a || {};
  const [agreed, setAgreed] = useState(card.application_status === "agreed");
  const [busy, setBusy] = useState(false);
  const [live, setLive] = useState(null);

  // F-M4-2: the attachment status is a snapshot from when the card was shared/dropped.
  // On mount fetch the LIVE request (status + server-computed is_owner) so Open/Closed
  // + the CTA reflect reality. Any error (e.g. a chat where the viewer lacks club
  // rights → 403/404) leaves the snapshot standing — never an error state.
  useEffect(() => {
    let active = true;
    if (!card.id) return undefined;
    friendliesService.getRequest(card.id).then((res) => {
      if (active && res.success) setLive(res.data);
    }).catch(() => {});
    return () => { active = false; };
  }, [card.id]);

  const club = card.club || {};
  const snapshotOpen = card.is_open ?? (card.status === "open") ?? (card.request_status === "open");
  const isOpen = live ? live.status === "open" : snapshotOpen;
  // F-M4-3: prefer the detail's authoritative per-viewer `is_owner` (server-computed
  // from viewer_club) over any client-side comparison. Snapshot fallback (live fetch
  // failed) = club_id match when the viewer's club id is known, else club_name. The FE
  // user carries no club_id today, so the name check is the offline fallback.
  const snapshotOwner = !!clubName && !!club.name && clubName.trim() === club.name.trim();
  const isOwner = live ? !!live.is_owner : snapshotOwner;
  // The specific application's status is carried on the card payload (the applicant's,
  // not the viewer's) — the live fetch only refreshes request status + owner.
  const canMarkAgreed =
    !!card.application_id && isOwner && !!card.camp_multiple_matches && isOpen &&
    (card.application_status === "in_chat") && !agreed;

  const markAgreed = async () => {
    if (busy) return;
    setBusy(true);
    const res = await friendliesService.markAgreed(card.application_id);
    setBusy(false);
    if (res.success) setAgreed(true);
  };

  return (
    <div className={`max-w-[340px] rounded-xl p-4 border ${isOpen ? "bg-white border-[#E4E9F1]" : "bg-[#F6F8FB] border-[#E6EAF0]"} shadow-[0_1px_2px_rgba(16,24,40,0.05)]`} data-testid="friendly-request-unfurl">
      <div className="flex items-center justify-between gap-2 mb-2">
        <p className={`text-[10px] font-bold uppercase tracking-[0.08em] ${isOpen ? "text-[#98A5B8]" : "text-[#B0B9C6]"}`}>Friendly request</p>
        {!isOpen && <span className="inline-flex items-center rounded-full bg-[#EEF1F6] px-2 py-0.5 text-[10px] text-[#8A98AC]">Request closed</span>}
      </div>

      <div className="flex items-center gap-2.5">
        {club.crest ? (
          <img src={club.crest} alt="" className="w-8 h-8 rounded-full object-cover" referrerPolicy="no-referrer" />
        ) : (
          <span className="w-8 h-8 rounded-full bg-[#0B3E8F] text-white text-[11px] font-bold flex items-center justify-center">{initials(club.name)}</span>
        )}
        <div className="min-w-0">
          <p className="text-[14px] font-semibold text-[#14263D] truncate">{club.name}</p>
          <p className="text-[12px] text-[#8A98AC] truncate">
            {[card.team_category, card.team_label].filter(Boolean).join(" · ")}{card.date_phrase ? ` · ${card.date_phrase}` : ""}
          </p>
        </div>
      </div>

      <div className="flex items-center flex-wrap gap-2 mt-2.5">
        {card.location && <LocationModePill location={card.location} />}
        {card.tier && <span className="text-[12px] text-[#475467]">Opponent: <span className="font-semibold text-[#33475E]">{card.tier}</span></span>}
      </div>

      {agreed && (
        <p className="mt-3 flex items-center gap-1.5 rounded-lg bg-[#E7F6EF] px-3 py-2 text-[12px] font-semibold text-[#0E7B52]">
          <Check className="w-3.5 h-3.5" /> Agreed
        </p>
      )}

      {canMarkAgreed && (
        <button type="button" onClick={markAgreed} disabled={busy}
          className="mt-3 w-full flex items-center justify-center gap-1.5 rounded-lg border border-[#17B26A] text-[#0E7B52] text-[12.5px] font-semibold py-2 hover:bg-[#ECFDF5] disabled:opacity-60"
          data-testid="unfurl-mark-agreed">
          <Check className="w-3.5 h-3.5" /> {busy ? "Marking…" : "Mark agreed"}
        </button>
      )}

      {isOpen ? (
        <div className="mt-2.5 flex gap-2">
          <button type="button" onClick={() => navigate("/friendlies")}
            className="flex-1 flex items-center justify-center gap-1.5 rounded-lg border border-[#E0E6EE] text-[#33475E] text-[12.5px] font-semibold py-2 hover:bg-gray-50">
            Open request
          </button>
          <button type="button" onClick={() => navigate("/friendlies")}
            className="flex-1 flex items-center justify-center gap-1.5 rounded-lg bg-[#2283FF] text-white text-[12.5px] font-semibold py-2 hover:bg-[#1B6FE0]">
            Apply <ArrowRight className="w-3.5 h-3.5" />
          </button>
        </div>
      ) : (
        <button type="button" onClick={() => navigate("/friendlies")}
          className="mt-2.5 w-full flex items-center justify-center rounded-lg border border-[#E0E6EE] text-[#475467] text-[12.5px] font-semibold py-2 hover:bg-gray-50"
          data-testid="unfurl-view">
          View
        </button>
      )}
    </div>
  );
};

export default FriendlyRequestCard;
